import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { mapOffre, mapReservation } from '../utils';

export const useReservationsData = ({ user, selectedIntervenant }) => {
  const [offres, setOffres] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [intervenants, setIntervenants] = useState([]);
  const [loading, setLoading] = useState(true);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  const rolePath = useMemo(() => {
    const isAdmin = user?.role === 1 || user?.role === 2;
    return isAdmin ? 'admin' : 'intervenant';
  }, [user]);

  const config = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    }),
    [token]
  );

  // Liste des intervenants (filtre)
  useEffect(() => {
    const fetchIntervenants = async () => {
      if (!token) return;

      try {
        const res = await axios.get(
          `${apiUrl}/${rolePath}/intervenant/l/list_intervenant`,
          config
        );
        setIntervenants(res.data.result);
      } catch (err) {
        console.error('Erreur intervenants', err);
      }
    };

    fetchIntervenants();
  }, [apiUrl, rolePath, token, config]);

  // Offres + réservations
  useEffect(() => {
    const fetchAllData = async () => {
      if (!token) return;

      try {
        setLoading(true);
        const query = selectedIntervenant
          ? `?intervenantId=${selectedIntervenant}`
          : '';

        const [responseOffres, responseReservations] = await Promise.all([
          axios.get(
            `${apiUrl}/${rolePath}/offer/g/lo/list_offer${query}`,
            config
          ),
          axios.get(
            `${apiUrl}/${rolePath}/offer/resevration/l/all${query}`,
            config
          ),
        ]);

        setOffres(responseOffres.data.result.map(mapOffre));
        setReservations(responseReservations.data.result.map(mapReservation));
      } catch (error) {
        console.error('Erreur API:', error.response?.data || error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [apiUrl, rolePath, token, selectedIntervenant, config]);

  // Changement de statut (optimistic UI + rollback)
  const updateReservationStatus = useCallback(
    async (reservationId, newStatus) => {
      if (!newStatus) {
        toast.error('Statut invalide');
        return;
      }

      const previousStatus = reservations.find((r) => r.id === reservationId)
        ?.originalData?.status_reservation;

      const applyStatus = (status) =>
        setReservations((prev) =>
          prev.map((r) =>
            r.id === reservationId
              ? {
                  ...r,
                  status,
                  originalData: { ...r.originalData, status_reservation: status },
                }
              : r
          )
        );

      applyStatus(newStatus);

      try {
        await axios.post(
          `${apiUrl}/${rolePath}/reservations/update-status/up/${reservationId}`,
          { status: newStatus },
          config
        );
        toast.success('Statut mis à jour avec succès');
      } catch (err) {
        console.error(err);
        applyStatus(previousStatus);

        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            'Erreur lors de la mise à jour du statut'
        );
      }
    },
    [reservations, apiUrl, rolePath, config]
  );

  return {
    apiUrl,
    token, // 👈 Ajouté ici pour qu'il soit accessible dans PlanningPro
    intervenants,
    offres,
    reservations,
    loading,
    updateReservationStatus,
  };
};